<script setup lang="ts">
	import { passwordValidator } from '@/helpers/customValidators'
	import { useQueryClient } from '@tanstack/vue-query'
	import useVuelidate from '@vuelidate/core'
	import {
		helpers,
		minLength,
		required,
		requiredIf,
		sameAs,
	} from '@vuelidate/validators'
	import { isAxiosError } from 'axios'
	import { computed } from 'vue'
	import { useToast } from 'vue-toastification'

	import HasPermission from '@/components/HasPermission.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useAccount from '@/composables/useAccount'
	import useAuthUser from '@/composables/useAuthUser.ts'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import useReactiveChangeTracker from '@/composables/useReactiveChangeTracker'

	interface Props {
		userId: number
		userEmail: string
		userHasPasswordSet?: boolean
		namePermissionUpdate: string
		entityType: string
	}

	const props = defineProps<Props>()

	const emits = defineEmits<{
		(event: 'passwordChanged'): void
	}>()

	const toast = useToast()
	const { user } = useAuthUser()
	const { changePasswordAsync, changePasswordLoading, sendPasswordResetEmail } =
		useAccount()

	const {
		value: form,
		hasChanged,
		resetInitialValue,
		resetValue,
	} = useReactiveChangeTracker({
		currentPassword: '',
		password: '',
		passwordConfirmation: '',
	})

	const rules = computed(() => ({
		currentPassword: {
			required: helpers.withMessage(
				'La contraseña actual es requerida',
				requiredIf(() => !props.userHasPasswordSet)
			),
		},
		password: {
			required: helpers.withMessage(
				'La nueva contraseña es requerida',
				required
			),
			minLength: helpers.withMessage(
				'La nueva contraseña debe tener al menos 8 caracteres',
				minLength(8)
			),
			passwordValidator: helpers.withMessage(
				'La contraseña es muy débil',
				passwordValidator
			),
		},
		passwordConfirmation: {
			required: helpers.withMessage(
				'La confirmación de contraseña es requerida',
				required
			),
			sameAsPassword: helpers.withMessage(
				'La confirmación de contraseña debe ser igual a la nueva contraseña',
				sameAs(form.password)
			),
		},
	}))
	const formVuelidate = useVuelidate(rules, form)

	const queryClient = useQueryClient()

	function showValidationErrors() {
		formVuelidate.value.$touch()

		if (formVuelidate.value.$invalid) {
			toast.error('Por favor, corrija los errores del formulario')
			return true
		}
		return false
	}

	async function handleSavePassword() {
		if (showValidationErrors()) return
		try {
			await changePasswordAsync({ params: form })
			toast.success('Contraseña cambiada exitosamente')
			resetForm()

			await queryClient.invalidateQueries(['user'])
			await queryClient.refetchQueries(['user'])
		} catch (err: unknown) {
			handleAxiosError(err)
		}
	}

	function resetForm() {
		resetValue()
		resetInitialValue()
		formVuelidate.value.$reset()
	}

	function handleAxiosError(err: unknown) {
		if (isAxiosError(err)) {
			toast.error(err.response?.data?.message ?? err.message)
		}
		console.log(err)
	}

	const handleSendVerifyEmail = (email: string) => {
		sendPasswordResetEmail({
			params: { email: email },
			config: {
				raiseToast: true,
				onSuccessToast: 'Se ha enviado un correo de verificación al usuario.',
				onErrorToast:
					'No se ha podido enviar el correo de verificación al usuario.',
			},
		})
	}

	const { hasPermissionUser } = useHasPermissionUser()

	const isTheSamePerson = computed(() => {
		return props.userId === user.value?.id
	})
</script>

<template>
	<form @submit.prevent="handleSavePassword">
		<h2 class="text-xl font-medium mb-5">Cambiar contraseña</h2>
		<div class="w-full grid gap-5">
			<div
				:class="
					userId === user?.id ?
						'grid grid-cols-1 md:grid-cols-2 gap-[2rem] gap-y-[1.563rem]'
					:	'md:grid-cols-1'
				"
			>
				<StyledInput
					v-if="userId === user?.id"
					is-required
					v-model="form.password"
					:error="formVuelidate.password.$errors.at(0)?.$message ?? ''"
					label="Nueva contraseña"
					type="password"
				/>
				<StyledInput
					v-if="userId === user?.id"
					is-required
					v-model="form.passwordConfirmation"
					:error="
						formVuelidate.passwordConfirmation.$errors.at(0)?.$message ?? ''
					"
					label="Confirmar contraseña"
					type="password"
				/>
				<StyledInput
					v-if="userId === user?.id && !props.userHasPasswordSet"
					is-required
					v-model="form.currentPassword"
					:error="formVuelidate.currentPassword.$errors.at(0)?.$message ?? ''"
					label="Contraseña actual"
					type="password"
				/>

				<div
					v-if="!props.userHasPasswordSet"
					class="flex flex-col gap-4"
					:class="userId === user?.id ? 'items-center' : 'items-end'"
				>
					<p
						class="text-sm text-gray-900 mt-5 text-center"
						v-if="userId === user?.id"
					>
						La contraseña debe tener al menos 8 caracteres, incluyendo al menos
						un número, una letra mayúscula y un símbolo.
					</p>
					<p
						v-else
						class="text-sm font-semibold text-blue-900 border-l-4 border-blue-700 pl-2 py-1 bg-blue-100 rounded-md shadow-md"
					>
						No puede cambiar la contraseña directamente. Puede solicitar un
						restablecimiento de contraseña, y enviaremos un enlace al correo
						electrónico del usuario que está editando, para que pueda establecer
						una nueva contraseña.
					</p>
					<button
						@click.prevent="handleSendVerifyEmail(userEmail)"
						class="inline-block px-4 py-2 text-sm font-semibold text-[#253C70] border-2 border-[#253C70] rounded-lg transition-colors duration-300 hover:bg-[#253C70] hover:text-white"
					>
						{{
							userId === user?.id ?
								'¿Olvidó su contraseña?'
							:	'Solicitar restablecimiento de contraseña'
						}}
					</button>
				</div>
			</div>

			<StyledButton
				v-if="
					isTheSamePerson || hasPermissionUser(namePermissionUpdate, entityType)
				"
				:disabled="!hasChanged || changePasswordLoading"
				:label="hasChanged ? 'Guardar' : 'Sin cambios'"
				:class="
					hasChanged ? 'bg-[#253C70] text-white' : 'bg-gray-400 text-gray-200'
				"
				class="ml-auto"
				type="submit"
				:loading="changePasswordLoading"
			/>
		</div>
	</form>
</template>

<style scoped></style>
